import React, { useState, useEffect, useCallback } from 'react';
import { Layout, Typography, Card, Button, Row, Col, Collapse, Divider, message, Spin, Modal } from 'antd';
import jsPDF from 'jspdf';
import axios from 'axios';
import { useUser } from '../context/UserContext';
import { Filesystem, Directory } from '@capacitor/filesystem';
import { Capacitor } from '@capacitor/core';
import { Share } from '@capacitor/share';
import AppHeader from './Header'; // Assicurati che il percorso del file sia corretto
import { apiUrl } from './config';  // Modifica il percorso in base alla posizione del file
import { useNavigate } from 'react-router-dom';
import '../App.css'
import { Preferences } from '@capacitor/preferences';
import { urlPlayer, urlYouTube } from '../video';




const { Content } = Layout;
const { Title, Text } = Typography;
const { Panel } = Collapse;


const ClienteDashboard = () => {
    const { user } = useUser();
    // La pagina e' raggiungibile anche senza sessione: all'avvio l'utente non
    // e' ancora stato letto dalle preferenze, e dopo una disconnessione resta
    // vuoto. Leggere user.email direttamente faceva cadere la schermata.
    const email = user?.email;
    const [workoutPlans, setWorkoutPlans] = useState([]);
    const [loading, setLoading] = useState(false);
    const [modalVisible, setModalVisible] = useState(false);
    const [selectedVideoUrl, setSelectedVideoUrl] = useState('');
    const [showPopup, setShowPopup] = useState(false);
    const navigate = useNavigate();
    const [collapseText, setCollapseText] = useState("Mostra Esercizi");
    const [olderCollapseText, setOlderCollapseText] = useState("Mostra Esercizi");

    const fetchWorkoutPlans = useCallback(async () => {
        if (!email) return;

        setLoading(true);
        const token = (await Preferences.get({ key: 'token' })).value;
        if (!token) {
            message.error('Token mancante. Effettua di nuovo il login.');
            setLoading(false);
            return;
        }

        try {
            const response = await axios.get(`${apiUrl}/api/workoutRequests/myPlan`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
                params: {
                    email,
                },
            });

            const assignedPlans = response.data.filter(plan => plan.workoutPlan);
            setWorkoutPlans(assignedPlans);
        } catch (error) {
            console.error('Errore durante il recupero dei piani di allenamento:', error.response?.data || error.message);
        } finally {
            setLoading(false);
        }
    }, [email]);

    useEffect(() => {
        // Senza sessione non c'e' niente da mostrare: si torna all'accesso.
        if (!email) {
            navigate('/login');
            return undefined;
        }

        fetchWorkoutPlans(); // Mantieni questa funzione per il recupero dei piani
    
        // Logica per mostrare il popup e far lampeggiare l'icona
        setShowPopup(true);
    
        const timer = setTimeout(() => {
          setShowPopup(false); // Nascondi il popup e fermare l'animazione dopo 3 secondi
        }, 4000);
    
        return () => clearTimeout(timer); // Pulizia del timeout se il componente si smonta
    }, [fetchWorkoutPlans, email, navigate]);
    

    const handleVideoOpen = (videoUrl) => {
        // Si tiene il link originale: la conversione in indirizzo del player
        // sta in video.js, cosi' e' una sola per tutta l'app.
        setSelectedVideoUrl(videoUrl);
        setModalVisible(true);
    };

    const handleModalClose = () => {
        setModalVisible(false);
        setSelectedVideoUrl(''); // Clear the video URL when closing
    };

    const groupExercisesByDayAndMuscle = (exercises, cardioAbsExercises = []) => {
        const groupedByDay = exercises.reduce((days, exercise) => {
            const day = exercise.day || 'Altro';
            if (!days[day]) {
                days[day] = {};
            }
            const muscleGroup = exercise.muscleGroup || 'Altro';
            if (!days[day][muscleGroup]) {
                days[day][muscleGroup] = [];
            }
            days[day][muscleGroup].push(exercise);
            return days;
        }, {});
    
        if (cardioAbsExercises.length > 0) {
            groupedByDay['Cardio/Addominali'] = { 'Cardio/Addominali': cardioAbsExercises };
        }
    
        return groupedByDay;
    };

    
    const generatePDF = async (plans) => {
        const plan = plans.workoutPlan;
        if (!plan) {
            message.error("Piano di allenamento non trovato.");
            return;
        }
    
        const doc = new jsPDF();
        const imgLogo = '/images/logo.png';
    
        // Aggiungi il logo e i dettagli del piano di allenamento
        doc.addImage(imgLogo, 'PNG', 10, 5, 50, 30);
        doc.setFontSize(16);
        doc.setFont('Helvetica', 'bold');
        doc.text(`${plan?.workoutPlanName || 'Scheda di Allenamento'}`, 120, 20);
    
        // Aggiungi la durata della scheda
        if (plan.duration) {
            doc.setFontSize(12);
            doc.setFont('Helvetica', 'bold');
            doc.text(`Durata: ${plan.duration.value} ${plan.duration.unit}`, 10, 40);
        }

        if (plan.notes) {
            doc.setFontSize(12);
            doc.text(`Note Scheda: ${plan.notes}`, 10, 40)
        }
    
        let yOffset = 50;
        const groupedByDayAndMuscle = groupExercisesByDayAndMuscle(plan.exercises || [], plan.cardioAbsExercises || []);
    
        Object.keys(groupedByDayAndMuscle).forEach((day) => {
            const exercisesByMuscleGroup = groupedByDayAndMuscle[day];
            let exerciseNumber = 1; // Resetta il numero degli esercizi per ogni giorno
    
            const validMuscleGroups = Object.keys(exercisesByMuscleGroup).filter((muscleGroup) =>
                exercisesByMuscleGroup[muscleGroup].some(exercise => ((exercise.series && exercise.reps) || exercise.note))
            );
    
            if (validMuscleGroups.length > 0) {
                doc.setFont('Helvetica', 'bold');
                doc.setFontSize(12);
                doc.text(`Giorno ${day}`, 5, yOffset);
                yOffset += 5;
    
                validMuscleGroups.forEach((muscleGroup) => {
                    const exercises = exercisesByMuscleGroup[muscleGroup]
                        .filter(exercise => (exercise.series && exercise.reps) || exercise.note)
                        .sort((a, b) => a.numberExercise - b.numberExercise);
    
                    yOffset += 5;
    
                    if (exercises.length > 0) {
                        doc.setFont('Helvetica', 'bold');
                        doc.setFontSize(10);
                        doc.text(`Gruppo Muscolare: ${muscleGroup}`, 5, yOffset);
                        yOffset += 5;
    
                        exercises.forEach((exercise) => {
                            const imgData = exercise.image;
                            const imageHeight = 30;
                            const imageWidth = 30;
    
                            doc.setDrawColor(150);
                            doc.setLineWidth(0.1);
                            doc.line(10, yOffset, 200, yOffset);
    
                            yOffset += 2;
    
                            if (exercise.image) {
                                doc.addImage(imgData, 'JPEG', 10, yOffset, imageWidth, imageHeight);
                            }
    
                            const rowHeight = 6;
                            let currentYOffset = yOffset + 5;
    
                            doc.setFont('Helvetica', 'bold');
                            doc.setFontSize(9);
                            
                            const exerciseNameLines = doc.splitTextToSize(`Esercizio ${exerciseNumber++}: ${exercise.name}`, 130);
                            doc.text(exerciseNameLines, 50, currentYOffset);
                            currentYOffset += rowHeight * exerciseNameLines.length;
    
                            doc.setFont('Helvetica', 'normal');
                            
                            const seriesText = doc.splitTextToSize(`Serie: ${exercise.series}`, 130);
                            const repsText = doc.splitTextToSize(`Ripetizioni: ${exercise.reps}`, 130);
                            doc.text(seriesText, 50, currentYOffset);
                            doc.text(repsText, 100, currentYOffset);
                            currentYOffset += rowHeight;
    
                            if (exercise.rest) {
                                const restText = doc.splitTextToSize(`Riposo: ${exercise.rest} sec`, 130);
                                doc.text(restText, 50, currentYOffset);
                                currentYOffset += rowHeight * restText.length;
                            }
    
                            if (exercise.note) {
                                const noteText = doc.splitTextToSize(`Note: ${exercise.note}`, 130);
                                doc.text(noteText, 50, currentYOffset);
                                currentYOffset += rowHeight * noteText.length;
                            }
    
                            yOffset = currentYOffset + rowHeight + 10;
    
                            if (yOffset > 270) {
                                doc.addPage();
                                yOffset = 10;
                            }
                        });
                        yOffset += 5;
                    }
                });
                yOffset += 10;
            }
        });
    
        const pdfBlob = doc.output('blob');
    
        if (Capacitor.isNativePlatform()) {
            const reader = new FileReader();
            reader.readAsDataURL(pdfBlob);
            reader.onloadend = async () => {
                const base64Data = reader.result.split(',')[1];
                try {
                    const fileName = `FitLab-Scheda-Allenamento.pdf`;
    
                    const writeFileResult = await Filesystem.writeFile({
                        path: `FitLab/${fileName}`,
                        data: base64Data,
                        directory: Directory.Documents,
                        recursive: true,
                    });
    
                    await Share.share({
                        title: 'Scarica il tuo PDF',
                        url: writeFileResult.uri,
                        dialogTitle: 'Condividi il piano di allenamento',
                    });
                } catch (e) {
                    console.error('Errore durante il salvataggio del file', e);
                }
            };
        } else {
            const url = URL.createObjectURL(pdfBlob);
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `FitLab-Scheda-Allenamento.pdf`);
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        }
    };
    
    
    

    
    
    return (
        <Layout style={{ minHeight: '100vh' }}>
            <AppHeader shouldPulse={showPopup} />
    
            <Content
                style={{
                    maxWidth: '100%',
                    margin: '0 auto',
                    paddingBottom: '50px',
                    paddingTop: '20px'
                }}
            >
                {/* Il nome di chi sta usando l'app dice piu' di "Dashboard Cliente",
                    che e' un termine da programmatori. */}
                <div className="dashboard-title">
                    {user?.nome ? `CIAO ${String(user.nome).trim().toUpperCase()}` : 'IL TUO ALLENAMENTO'}
                </div>
    
                {loading ? (
                    <Spin style={{ display: 'block', marginTop: '2rem' }} />
                ) : workoutPlans.length > 0 ? (
                    <>
                        <Card
                            style={{
                                marginTop: '2rem',
                                width: '100%',
                            }}
                        >
                            <div className="subtitle">LA TUA SCHEDA</div>
                            {/* Il nome della scheda non compare: serve al trainer per
                                organizzare il proprio archivio, al cliente non dice nulla.
                                La data invece gli fa capire se e' quella nuova. */}
                            {workoutPlans[0].request?.assignedAt && (
                                <div className="text">
                                    Assegnata il {new Date(workoutPlans[0].request.assignedAt).toLocaleDateString('it-IT')}
                                </div>
                            )}
                            <Divider />
                            <Collapse
                                onChange={() => {
                                    setCollapseText(prev => prev === "Mostra Esercizi" ? "Nascondi Esercizi" : "Mostra Esercizi");
                                }}
                            >
                                <Panel header={<span style={{ color: "#ffffff" }}>{collapseText}</span>} key="mostRecentPlan" style={{ backgroundColor: "var(--primario)" }}>
                                    {workoutPlans[0].workoutPlan?.duration && (
                                        <Text style={{ fontSize: '14px', fontWeight: 'bold', color: 'var(--inchiostro)', display: 'block', marginBottom: '5px' }}>
                                            Durata: {workoutPlans[0].workoutPlan?.duration.value} {workoutPlans[0].workoutPlan?.duration.unit}
                                        </Text>
                                    )}
                                    {workoutPlans[0].workoutPlan?.notes && (
                                        <Text style={{ fontSize: '14px', fontWeight: 'bold', color: 'var(--inchiostro)', display: 'block', marginBottom: '5px' }}>
                                            Note Scheda: {workoutPlans[0].workoutPlan?.notes}
                                        </Text>
                                    )}
                                    {Object.keys(groupExercisesByDayAndMuscle(workoutPlans[0].workoutPlan?.exercises, workoutPlans[0].workoutPlan?.cardioAbsExercises))
    .filter(day => day !== 'Altro')
    .map((day, dayIdx) => (
        <div key={dayIdx}>
            <Title level={5} style={{ fontSize: '24px' }}>
                {day === 'Cardio/Addominali' ? 'Sezione Cardio/Addominali' : `Giorno ${day}`}
            </Title>
            {(() => {
                let currentExerciseNumber = 1;
                return Object.keys(groupExercisesByDayAndMuscle(workoutPlans[0].workoutPlan?.exercises, workoutPlans[0].workoutPlan?.cardioAbsExercises)[day])
                    .filter(muscleGroup =>
                        groupExercisesByDayAndMuscle(workoutPlans[0].workoutPlan?.exercises, workoutPlans[0].workoutPlan?.cardioAbsExercises)[day][muscleGroup]
                            .some(exercise => (exercise.series && exercise.reps) || exercise.note)
                    )
                    .map((muscleGroup, muscleIdx) => (
                        <div key={muscleIdx}>
                            <Title level={6} style={{ fontSize: '20px' }}>
                                {muscleGroup}
                            </Title>
                            <Row gutter={[16, 16]}>
                                {groupExercisesByDayAndMuscle(workoutPlans[0].workoutPlan?.exercises, workoutPlans[0].workoutPlan?.cardioAbsExercises)[day][muscleGroup]
                                    .filter(exercise => (exercise.series && exercise.reps) || exercise.note)
                                    .sort((a, b) => a.numberExercise - b.numberExercise)
                                    .map((exercise, exerciseIdx) => (
                                        <Col xs={24} key={exerciseIdx}>
                                            <div style={{ display: 'flex', alignItems: 'center' }}>
                                                <img
                                                    src={exercise.image}
                                                    alt={exercise.name}
                                                    style={{ height: '90px', width: '90px', marginRight: '20px', objectFit: 'cover' }}
                                                />
                                                <div style={{ textAlign: 'left' }}>
                                                    <Text strong>Esercizio {currentExerciseNumber++}: </Text> {exercise.name}
                                                    <br />
                                                    <Text strong>Serie: </Text> {exercise.series}
                                                    <br />
                                                    <Text strong>Ripetizioni: </Text> {exercise.reps}
                                                    <br />
                                                    {exercise.rest && (
                                                        <>
                                                            <Text strong>Riposo: </Text> {exercise.rest} sec
                                                            <br />
                                                        </>
                                                    )}
                                                    {exercise.note && (
                                                        <>
                                                            <Text strong>Note: </Text> {exercise.note}
                                                            <br />
                                                        </>
                                                    )}
                                                    {exercise.video && (
                                                        <Button 
                                                            onClick={() => handleVideoOpen(exercise.video)}
                                                            className="button-no-background"
                                                            style={{ marginTop: '5px', alignSelf: 'center' }}
                                                        >
                                                            Vedi Video Esercizio
                                                        </Button>
                                                    )}
                                                </div>
                                            </div>
                                            <Divider />
                                        </Col>
                                    ))}
                            </Row>
                        </div>
                    ));
            })()}
            <Divider />
        </div>
    ))}


                                </Panel>
                            </Collapse>
                            <Button
                                type="default"
                                onClick={() => generatePDF(workoutPlans[0])}
                                style={{ marginTop: '1rem', width: '100%', backgroundColor: "var(--primario)", color: "#ffffff" }}
                            >
                                Scarica PDF
                            </Button>
                        </Card>
    
                        <Collapse style={{ marginTop: '2rem', width: '100%' }}>
                            <Panel header="Piani meno recenti" key="olderPlans">
                                {workoutPlans.slice(1).map((plan, idx) => (
                                    <Card key={idx} style={{ marginTop: '1rem', width: '100%' }}>
                                        <Title level={4}>Piano di Allenamento #{idx + 2}</Title>
                                        <Text>Nome: {plan.workoutPlan?.name || 'N/A'}</Text>
                                        <Divider />
                                        <Collapse
                                            onChange={() => {
                                                setOlderCollapseText(prev => prev === "Mostra Esercizi" ? "Nascondi Esercizi" : "Mostra Esercizi");
                                            }}
                                            style={{ width: '100%' }}
                                        >
                                            <Panel header={<span style={{ color: "#ffffff" }}>{olderCollapseText}</span>} key="olderPlans" style={{ backgroundColor: "var(--primario)", width: '100%' }}>
                                                {plan.workoutPlan.duration && (
                                                    <Text style={{ fontSize: '14px', fontWeight: 'bold', color: 'var(--primario)', display: 'block', marginBottom: '5px' }}>
                                                        Durata: {plan.workoutPlan.duration.value} {plan.workoutPlan.duration.unit}
                                                    </Text>
                                                )}
                                                {plan.workoutPlan.notes && (
                                                    <Text style={{ fontSize: '14px', fontWeight: 'bold', color: 'var(--primario)', display: 'block', marginBottom: '5px' }}>
                                                        Note Scheda: {plan.workoutPlan.notes}
                                                    </Text>
                                                )}
                                                {Object.keys(groupExercisesByDayAndMuscle(plan.workoutPlan?.exercises, plan.workoutPlan?.cardioAbsExercises))
                                                    .filter(day => day !== 'Altro')
                                                    .map((day, dayIdx) => (
                                                        <div key={dayIdx}>
                                                            <Title level={5} style={{ fontSize: '24px' }}>
                                                                Giorno {day}
                                                            </Title>
                                                            {Object.keys(groupExercisesByDayAndMuscle(plan.workoutPlan?.exercises, plan.workoutPlan?.cardioAbsExercises)[day])
                                                                .filter(muscleGroup =>
                                                                    groupExercisesByDayAndMuscle(plan.workoutPlan?.exercises, plan.workoutPlan?.cardioAbsExercises)[day][muscleGroup]
                                                                        .some(exercise => (exercise.series && exercise.reps) || exercise.note)
                                                                )
                                                                .map((muscleGroup, muscleIdx) => (
                                                                    <div key={muscleIdx}>
                                                                        <Title level={6} style={{ fontSize: '20px' }}>
                                                                            {muscleGroup}
                                                                        </Title>
                                                                        <Row gutter={[16, 16]}>
                                                                            {groupExercisesByDayAndMuscle(plan.workoutPlan?.exercises, plan.workoutPlan?.cardioAbsExercises)[day][muscleGroup]
                                                                                .filter(exercise => (exercise.series && exercise.reps) || exercise.note)
                                                                                .sort((a, b) => a.numberExercise - b.numberExercise)
                                                                                .map((exercise, exerciseIdx) => (
                                                                                    <Col xs={24} key={exerciseIdx}>
                                                                                        <div style={{ display: 'flex', alignItems: 'center' }}>
                                                                                            <img
                                                                                                src={exercise.image}
                                                                                                alt={exercise.name}
                                                                                                style={{ height: '90px', width: '90px', marginRight: '20px', objectFit: 'cover' }}
                                                                                            />
                                                                                            <div style={{ textAlign: 'left' }}>
                                                                                                <Text strong>Esercizio {exerciseIdx + 1}: </Text>
                                                                                                {exercise.name}
                                                                                                <br />
                                                                                                <Text strong>Serie: </Text>
                                                                                                {exercise.series}
                                                                                                <br />
                                                                                                <Text strong>Ripetizioni: </Text>
                                                                                                {exercise.reps}
                                                                                                <br />
                                                                                                {exercise.rest && (
                                                                                                    <>
                                                                                                        <Text strong>Riposo: </Text>
                                                                                                        {exercise.rest} sec
                                                                                                        <br />
                                                                                                    </>
                                                                                                )}
                                                                                                {exercise.note && (
                                                                                                    <>
                                                                                                        <Text strong>Note: </Text>
                                                                                                        {exercise.note}
                                                                                                        <br />
                                                                                                    </>
                                                                                                )}
                                                                                                {exercise.video && (
                                                                                                    <div style={{ display: 'flex', justifyContent: 'flex-start' }}>
                                                                                                        <Button 
                                                                                                            onClick={() => handleVideoOpen(exercise.video)}
                                                                                                            className="button-no-background"
                                                                                                            style={{ marginTop: '5px', alignSelf: 'center' }}
                                                                                                        >
                                                                                                            Vedi Video Esercizio
                                                                                                        </Button>
                                                                                                    </div>
                                                                                                )}
                                                                                            </div>
                                                                                        </div>
                                                                                        <Divider />
                                                                                    </Col>
                                                                                ))}
                                                                        </Row>
                                                                    </div>
                                                                ))}
                                                            <Divider />
                                                        </div>
                                                    ))}
                                            </Panel>
                                        </Collapse>
                                        <Button
                                            type="default"
                                            onClick={() => generatePDF(plan)}
                                            style={{ marginTop: '1rem', width: '100%', backgroundColor: "var(--primario)", color: "#ffffff" }}
                                        >
                                            Scarica PDF
                                        </Button>
                                    </Card>
                                ))}
                            </Panel>
                        </Collapse>
                    </>
                ) : (
                    <div style={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        textAlign: 'center'
                    }}>
                        <Text style={{ marginBottom: '1rem', display: 'block', fontSize: '18px', color: '#333', margin: '15px' }}>
                            Non hai ancora ricevuto un piano di allenamento dal tuo trainer. Richiedilo ora!
                        </Text>
                        <Button
                            type="primary"
                            style={{
                                backgroundColor: 'var(--primario)',
                                borderColor: 'var(--primario)',
                                fontSize: '18px',
                                padding: '10px 20px',
                                width: '200px',
                                height: '50px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                            }}
                            onClick={() => navigate('/new-scheda')}
                        >
                            RICHIEDI SCHEDA
                        </Button>
                    </div>
                )}
                <Divider style={{ marginTop: '2rem', marginBottom: '2rem' }} />
    
                <Card
                    style={{
                        marginTop: '2rem',
                        width: '100%',
                        backgroundColor: 'var(--inchiostro)',
                        border: '2px solid',
                    }}
                >
                    <Row justify="center" align="middle" style={{ padding: '20px' }}>
                        <Col xs={24} style={{ textAlign: 'center' }}>
                            <div className="wod-title">
                                Workout of the Day
                            </div>
                            <Text style={{ fontSize: '16px', color: '#ffffff', display: 'block', maxWidth: 320, margin: '0 auto' }}>
                                L'allenamento di oggi, uguale per tutta la palestra.
                            </Text>
                            <Button
                                type="primary"
                                size="large"
                                style={{
                                    backgroundColor: 'var(--primario)',
                                    marginTop: '15px',
                                    color: '#fff',
                                }}
                                onClick={() => {
                                    navigate('/daily-workout');
                                }}
                            >
                                Vedi il WOD di oggi
                            </Button>
                        </Col>
                    </Row>
                </Card>
    
                <Modal
                    title="Video Esercizio"
                    open={modalVisible}
                    getContainer={document.body}
                    onCancel={handleModalClose}
                    footer={null}
                    width={800}
                >
                    {urlPlayer(selectedVideoUrl) ? (
                        <>
                            <iframe
                                width="100%"
                                height="400"
                                src={urlPlayer(selectedVideoUrl)}
                                title="Video Esercizio"
                                frameBorder="0"
                                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                allowFullScreen
                            ></iframe>
                            <a
                                href={urlYouTube(selectedVideoUrl)}
                                target="_blank"
                                rel="noreferrer"
                                style={{ display: 'block', marginTop: '10px', textAlign: 'center', fontSize: '13px' }}
                            >
                                Se il video non parte, aprilo su YouTube
                            </a>
                        </>
                    ) : (
                        <p style={{ textAlign: 'center', margin: '30px 0' }}>Video non disponibile.</p>
                    )}
                </Modal>
            </Content>
        </Layout>
    );
}    

export default ClienteDashboard;